<template src="./image-classifier.component.html"></template>
<style scoped src="./image-classifier.component.css"></style>
<script lang="ts">
import Vue, {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import {type Route} from 'vue-router';
import {useRoute, useRouter} from 'vue-router/composables';
import Modal from '@/components/modal/modal.component.vue';
import Sidebar from '@/components/sidebar/sidebar.component.vue';
import Pagination from '@/components/pagination/pagination.component.vue';
import {toasted} from '@/plugins/toasted';
import {httpClient} from '@/api';
import {type ClassifiedImage, PaginatedResponse} from '@/models';
import {getErrorMessage, noop, Primitive, SearchParams} from '@/helpers';

const contentSafety = [
  'safe',
  'borderline',
  'adult',
  'unclear',
];
const policyCompliance = [
  'in policy',
  'not in policy',
];

const keywordAdherence = [{
  value: 1,
  label: 'adherent',
}, {
  value: 0,
  label: 'not adherent',
}];

const filterDefaultValues = {keyword: '', etag: '', policyCompliance: '', contentSafety: ''};

function getQueryFilterValue(query: Route['query'], key: keyof typeof filterDefaultValues) {
  const value = query[key];
  return typeof value === 'string' ? value : '';
}

export default Vue.extend({
  components: {
    Modal,
    Sidebar,
    Pagination,
  },
  filters: {
    label(value: Primitive, options: readonly {label: Primitive, value: Primitive}[]) {
      const option = options.find(option => option.value === value);
      return option ? option.label : value;
    },
  },
  setup() {
    const route = useRoute();
    const query = computed(() => Object.assign({page: '1'}, route.query));
    const router = useRouter();

    // data
    const data = ref(new PaginatedResponse<ClassifiedImage>());
    const showLoading = ref(false);
    const filter = ref({
      ...filterDefaultValues,
    });
    const filterOptions = {
      policyCompliance,
      contentSafety,
    };

    // watch
    watch(query, () => {
      filter.value = {
        keyword: getQueryFilterValue(query.value, 'keyword'),
        etag: getQueryFilterValue(query.value, 'etag'),
        policyCompliance: getQueryFilterValue(query.value, 'policyCompliance'),
        contentSafety: getQueryFilterValue(query.value, 'contentSafety'),
      };
      getImages(query.value);
    }, {immediate: true});

    // mounted
    onMounted(() => {
      document.body.classList.add(
        'min-w-site',
        'bg-40',
        'text-90',
        'font-medium',
        'min-h-full',
      );
    });
    // unmounted
    onUnmounted(() => {
      document.body.classList.remove(
        'min-w-site',
        'bg-40',
        'text-90',
        'font-medium',
        'min-h-full',
      );
    });

    // methods
    async function getImages(query: Route['query']) {
      try {
        showLoading.value = true;
        const params = SearchParams.createURLSearchParams(query);
        const res = await httpClient.get<PaginatedResponse<ClassifiedImage>>('/image-classifier', {params});
        data.value = res.data;
      } catch (e) {
        toasted.showErrorMessage(getErrorMessage(e));
      } finally {
        showLoading.value = false;
      }
    }
    function onFilter() {
      updateQueryParams({...filter.value, page: 1});
    }
    function onResetFilter() {
      filter.value = {...filterDefaultValues};
      updateQueryParams({...filter.value, page: 1});
    }
    function onPageChange(page: number) {
      updateQueryParams({...filter.value, page});
    }
    function updateQueryParams(values: {[key: string]: any}) {
      const query = SearchParams.mergeQueryParams(values, route.query);
      router.push({query}).catch(noop);
    }
    async function removeImage(image: ClassifiedImage) {
      try {
        await httpClient.delete(`/image-classifier/${image.etag}`);
        toasted.showSuccessMessage('Image deleted');
        data.value = PaginatedResponse.removeElement(data.value, image, 'etag');
      } catch (e) {
        toasted.showErrorMessage(getErrorMessage(e));
      }
    }

    return {
      data, showLoading, filter, filterOptions, keywordAdherence,
      onFilter, onResetFilter, onPageChange, removeImage,
    };
  },
});
</script>
